import { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { ArrowRight, Save } from "lucide-react";
import { academyContentAPI, academyQuestionsAPI, getApiError } from "../../services/academyService";
import { ACADEMY_ROOT, DIFFICULTY_OPTIONS } from "./academyConstants";
import QuestionTextEditor from "./components/QuestionTextEditor";
import MediaDropField from "./components/MediaDropField";
import { findContentItem } from "./utils/findContentItem";

const PARENT_CFG = {
  "reading-passage": {
    label: "قطعة قراءة",
    fields: [
      { name: "title", label: "العنوان", required: true, type: "text" },
      { name: "passage_text", label: "نص القطعة", required: true, type: "richtext" },
      { name: "source", label: "المصدر", required: false, type: "text" },
    ],
    image: { name: "passage_image", label: "صورة القطعة (اختياري)" },
  },
  "listening-audio": {
    label: "تسجيل صوتي",
    fields: [
      { name: "title", label: "العنوان", required: true, type: "text" },
      { name: "transcript", label: "النص المكتوب", required: false, type: "richtext" },
      { name: "duration", label: "المدة بالثواني", required: false, type: "number" },
    ],
    image: { name: "audio_image", label: "صورة (اختياري)" },
    file: { name: "audio_file", label: "الملف الصوتي", required: true, accept: "audio/*", kind: "audio" },
  },
  "speaking-video": {
    label: "فيديو تحدث",
    fields: [
      { name: "title", label: "العنوان", required: true, type: "text" },
      { name: "description", label: "الوصف", required: false, type: "richtext" },
      { name: "duration", label: "المدة بالثواني", required: false, type: "number" },
    ],
    image: { name: "video_image", label: "صورة (اختياري)" },
    file: { name: "video_file", label: "ملف الفيديو", required: true, accept: "video/*", kind: "video" },
  },
};

// الـ rich text editor بيرجّع "<p></p>" لما يكون فاضي، فنحوّله لنص فاضي قبل الإرسال
const isRichEmpty = (html) => !html || html.replace(/<[^>]*>/g, "").trim() === "";
const cleanRich = (html) => (isRichEmpty(html) ? "" : html);

export default function AcademyParentForm({ kind, skillId, itemId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = Boolean(itemId);
  const cfg = PARENT_CFG[kind];
  const backPath = `${ACADEMY_ROOT}/skills/${skillId}`;

  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(isEdit);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    difficulty: "MEDIUM",
    is_active: true,
    ...Object.fromEntries(cfg.fields.map((f) => [f.name, ""])),
  });
  const [imageFile, setImageFile] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [imageCleared, setImageCleared] = useState(false);
  const [mediaFile, setMediaFile] = useState(null);
  const [existingMediaUrl, setExistingMediaUrl] = useState(null);

  useEffect(() => {
    if (!isEdit) return;
    const passed = location.state?.item;
    if (passed) {
      applyItem(passed);
      setInitLoading(false);
      return;
    }
    (async () => {
      try {
        const data = await academyQuestionsAPI.getSkillQuestions(skillId, 1, 200);
        const found = findContentItem(data.questions || [], kind, itemId);
        if (found) applyItem(found);
        else setErrors({ general: "تعذر تحميل البيانات" });
      } catch (err) {
        setErrors({ general: getApiError(err, "تعذر تحميل البيانات") });
      } finally {
        setInitLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, itemId]);

  const applyItem = (item) => {
    setForm({
      difficulty: item.difficulty || "MEDIUM",
      is_active: item.is_active ?? true,
      ...Object.fromEntries(cfg.fields.map((f) => [f.name, item[f.name] ?? ""])),
    });
    if (cfg.image) setExistingImageUrl(item[cfg.image.name] || null);
    if (cfg.file) setExistingMediaUrl(item[cfg.file.name] || null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    for (const f of cfg.fields) {
      if (!f.required) continue;
      const empty = f.type === "richtext" ? isRichEmpty(form[f.name]) : !String(form[f.name] || "").trim();
      if (empty) newErrors[f.name] = `${f.label} مطلوب`;
    }
    if (!isEdit && cfg.file?.required && !mediaFile) newErrors[cfg.file.name] = `${cfg.file.label} مطلوب`;
    if (Object.keys(newErrors).length) return setErrors(newErrors);

    setLoading(true);
    setErrors({});
    try {
      const fd = new FormData();
      cfg.fields.forEach((f) => {
        if (f.type === "number" && form[f.name] === "") return; // ما تبعتش لو فاضية
        const v = form[f.name] ?? "";
        fd.append(f.name, f.type === "richtext" ? cleanRich(v) : v);
      });
      fd.append("difficulty", form.difficulty);
      fd.append("is_active", form.is_active ? "True" : "False");
      if (!isEdit) fd.append("academy_skill", skillId);

      if (cfg.image) {
        if (imageFile) fd.append(cfg.image.name, imageFile);
        else if (isEdit && imageCleared) fd.append(cfg.image.name, "");
      }
      if (cfg.file && mediaFile) fd.append(cfg.file.name, mediaFile);

      if (isEdit) await academyContentAPI.update(kind, itemId, fd);
      else await academyContentAPI.create(kind, fd);
      navigate(backPath);
    } catch (err) {
      setErrors(err.response?.data || { general: "حدث خطأ، حاول مرة أخرى" });
    } finally {
      setLoading(false);
    }
  };

  if (initLoading) return <div className="max-w-xl mx-auto py-16 text-center text-gray-400">جارِ التحميل...</div>;

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link to={backPath} className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors">
          <ArrowRight className="w-5 h-5" />
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">{isEdit ? "تعديل" : "إضافة"} {cfg.label}</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5 bg-white rounded-2xl border border-gray-200 p-6">
        {cfg.fields.map((f) => (
          <div key={f.name}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {f.label} {f.required && <span className="text-red-500">*</span>}
            </label>
            {f.type === "richtext" ? (
              <QuestionTextEditor value={form[f.name]} onChange={(html) => setForm((p) => ({ ...p, [f.name]: html }))} />
            ) : (
              <input type={f.type} name={f.name} value={form[f.name]} onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            )}
            {errors[f.name] && <p className="text-red-500 text-xs mt-1">{errors[f.name]}</p>}
          </div>
        ))}

        {cfg.image && (
          <MediaDropField label={cfg.image.label} kind="image" accept="image/*"
            file={imageFile} existingUrl={existingImageUrl} cleared={imageCleared}
            onChange={setImageFile} onClear={() => setImageCleared(true)} />
        )}

        {cfg.file && (
          <>
            <MediaDropField label={cfg.file.label} kind={cfg.file.kind} accept={cfg.file.accept}
              required={cfg.file.required && !isEdit}
              file={mediaFile} existingUrl={existingMediaUrl} onChange={setMediaFile} />
            {errors[cfg.file.name] && <p className="text-red-500 text-xs mt-1">{errors[cfg.file.name]}</p>}
          </>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">مستوى الصعوبة</label>
          <select name="difficulty" value={form.difficulty} onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500">
            {DIFFICULTY_OPTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
          نشط
        </label>

        {errors.general && <p className="text-red-500 text-sm bg-red-50 p-3 rounded-lg">{errors.general}</p>}

        <button type="submit" disabled={loading}
          className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white py-3 rounded-lg font-medium transition-colors">
          <Save className="w-5 h-5" />
          {loading ? "جارِ الحفظ..." : "حفظ"}
        </button>
      </form>
    </div>
  );
}